import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";

import {
  CASES_KEY,
  INCOMING_KEY,
  NOTICES_KEY,
  ProblemError,
  RECORD_KEY,
  refusalCode,
  requestHandover,
  USERS_KEY,
  useUsers,
  type HandoverOut,
} from "@/api";
import { Button } from "@/components/Button";
import { Dialog, DialogFooter } from "@/components/Dialog";
import { SelectField } from "@/components/fields/SelectField";
import { TextArea } from "@/components/fields/TextArea";
import { Figures } from "@/components/Figures";
import { StateMessage } from "@/components/StateMessage";
import { activityTime, dayInDays, formatDay } from "@/lib/format";

import styles from "./Handover.module.css";
import type { PreviousRequest } from "./previousRequest";

/** One sentence per refusal code (contract §3.2 `HandoverRefused.code`). */
export const REQUEST_REFUSED: Record<string, string> = {
  already_pending: "Für diesen Vorgang läuft schon eine Übergabe-Anfrage.",
  self: "An dich selbst lässt sich ein Vorgang nicht übergeben.",
  recipient_unknown: "Diese Person gibt es nicht mehr — wähl bitte jemand anderen.",
  recipient_inactive: "Diese Person ist nicht mehr aktiv — wähl bitte jemand anderen.",
  message_empty: "Ohne Nachricht geht keine Übergabe raus.",
  case_finished: "Der Vorgang ist beendet — er lässt sich nicht mehr übergeben.",
  not_owner_anymore: "Der Vorgang liegt inzwischen nicht mehr bei dir.",
};

/** Refusals that are about the person chosen — said at the „An" field, which is chosen again. */
const AT_RECIPIENT = new Set(["self", "recipient_unknown", "recipient_inactive"]);

const NO_PERSON = "Wähl aus, an wen der Vorgang gehen soll.";
const NO_MESSAGE = REQUEST_REFUSED.message_empty ?? "Ohne Nachricht geht keine Übergabe raus.";
const NOBODY = "Im Team ist noch niemand, an den du übergeben kannst.";
const TEAM_DOWN = "Das Team ist gerade nicht erreichbar — versuch es gleich noch einmal.";

/**
 * The refusal at the „An" field names whom it is about (record fix round 2,
 * M-6): the field is reset to „Person wählen" in the same moment, and „Diese
 * Person" then pointed at nobody on screen. The name is the one chosen when
 * the request went out; without it, the general sentence.
 */
function recipientRefusal(code: string, name: string | null): string {
  if (name !== null && code === "recipient_inactive") return `${name} ist nicht mehr aktiv — wähl bitte jemand anderen.`;
  if (name !== null && code === "recipient_unknown") return `${name} gibt es im Team nicht mehr — wähl bitte jemand anderen.`;
  return REQUEST_REFUSED[code] ?? NO_PERSON;
}

interface HandoverDialogProps {
  caseId: string;
  caseTitle: string;
  caseNo: string;
  /**
   * Whom the open request the record shows goes to — known once the record
   * has been read again after a 409 `already_pending` (screen check S-6).
   */
  pendingRecipient?: string | null;
  onClose: () => void;
  /** After the dialog is gone and the focus is back — see `Dialog.onClosed`. */
  onClosed?: () => void;
  onRequested: (handover: HandoverOut) => void;
  /**
   * 409 `already_pending`: a request is already out. The dialog stays open
   * with its sentence and only „Laufende Anfrage ansehen" (screen check S-6); the record
   * has been read again and shows the request — the caller takes the focus
   * to its waiting line once the dialog closes.
   */
  onAlreadyPending: () => void;
  /**
   * „Abbrechen" after a 409 `already_pending` (R2-8): close without going to
   * the running request — the caller drops the way to its waiting line, and
   * the focus goes back to the opener as after any other „Abbrechen".
   */
  onCancelRunning?: () => void;
  /** 404: the Vorgang is somebody else's since a moment ago — the record reloads into „nicht gefunden" (I3). */
  onGone: () => void;
  /**
   * How the last request ended, when the record says so (`previousRequest`,
   * R3-12): a refusal stands over „An" with who, why and when, and that
   * person is marked in the list; after a lapse the same person is chosen,
   * and the same place over „An" says why (round-4 screen check, R4-14).
   */
  previous?: PreviousRequest | null;
}

/** The one person in the team with this name — none when two share it (N-9), none when they left. */
function onlyOne(people: readonly { id: string; displayName: string }[], name: string): string | null {
  const found = people.filter((person) => person.displayName === name);
  return found.length === 1 ? found[0]!.id : null;
}

/**
 * „Vorgang übergeben" (design §8.3, D-4). The message is required — the
 * colleague takes over a Vorgang, not a number — and the consequences stand
 * as a short list before the buttons (UX ruling 10): it stays mine until
 * accepted; afterwards it lies with the colleague, I no longer see it, and
 * the calculation and its approval go along; without an answer it lapses —
 * on a named day, not „after 7 days".
 *
 * Errors stand at their field and take the focus; a refusal of the server
 * keeps the dialog open with its sentence (review focus 3): the person typed
 * a message, and closing on them would throw it away. `busy` on the `Dialog`
 * itself keeps Escape from doing the same while the request is in flight
 * (task 11 controller addendum).
 *
 * While the request is out the button stays where the focus is — marked
 * `aria-disabled`, not `disabled` (record fix wave I1): a disabled button
 * drops the focus to `<body>`, and after the answer Tab never came back in.
 * The 422 names its reason since the same wave (I4): a person the server
 * turns away is said at the „An" field, which is chosen again; a Vorgang
 * finished meanwhile holds the button with the reason.
 *
 * Screen check S-5: an empty person and an empty message are said at both
 * fields at once, the focus on the first. S-6: a 409 `already_pending` no
 * longer closes the dialog on the typed message — it waits for the record
 * read again, names whose request is running, keeps the message, and its
 * one way on is „Laufende Anfrage ansehen" — the consequences list, which
 * described a request about to go out, gives way to „Deine Anfrage an … ist
 * nicht rausgegangen." (UX gate round 1, S-6 remainder). Round-2 screen
 * check, R2-8: the two fields then stand read-only — what was typed stays in
 * view, but editable fields promised a request that can no longer go out —
 * and „Abbrechen" stands beside the way to the running request again.
 */
export function HandoverDialog({
  caseId,
  caseTitle,
  caseNo,
  pendingRecipient = null,
  onClose,
  onClosed,
  onRequested,
  onAlreadyPending,
  onCancelRunning,
  onGone,
  previous = null,
}: HandoverDialogProps) {
  const client = useQueryClient();
  const users = useUsers();
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  // `null` until a person is picked: after a lapse the person the request
  // went to stands chosen (UX gate round 3, #4), once the team is known.
  const [picked, setToUserId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [toError, setToError] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  // 409 `already_pending` (S-6): nothing is left to ask — only to close.
  const [alreadyPending, setAlreadyPending] = useState(false);
  // …and the focus on that one button, wherever it was when the answer came.
  useEffect(() => {
    if (alreadyPending) submitRef.current?.focus();
  }, [alreadyPending]);
  const people = users.data ?? [];
  const declinedBy = previous?.outcome === "declined" ? onlyOne(people, previous.name) : null;
  const toUserId = picked ?? (previous?.outcome === "expired" ? onlyOne(people, previous.name) : null) ?? "";
  const recipient = people.find((user) => user.id === toUserId) ?? null;
  const nobody = users.isSuccess && people.length === 0;
  const teamDown = users.isError;
  // The one reason the button cannot ask, read out with it (F-4).
  const held = finished ? "handover-refused" : nobody || teamDown ? "handover-to-hint" : null;

  function focusTo() {
    document.getElementById("handover-to")?.focus();
  }

  /** Everything a request changes for the person asking: the record, the register, both handover lists. */
  function refresh() {
    for (const queryKey of [RECORD_KEY(caseId), CASES_KEY, INCOMING_KEY, NOTICES_KEY]) {
      void client.invalidateQueries({ queryKey });
    }
  }

  /**
   * S-6: read the record again first, so the sentence can name whose request
   * it is — shown once, with the name, rather than general first and named a
   * moment later (a changing `role="alert"` is read out twice). The button
   * says „Wird angefragt …" meanwhile, as before the answer.
   */
  async function alreadyRunning() {
    await client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
    refresh();
    setBusy(false);
    setAlreadyPending(true);
    onAlreadyPending();
  }

  function refuse(cause: unknown, asked: string | null) {
    const code = refusalCode(cause);
    if (cause instanceof ProblemError && cause.status === 404) {
      void client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
      onGone();
      return;
    }
    if (code !== null && AT_RECIPIENT.has(code)) {
      setToUserId("");
      setToError(recipientRefusal(code, asked));
      // The list is stale — somebody left or was renamed; ask it again.
      void client.invalidateQueries({ queryKey: USERS_KEY });
      focusTo();
      return;
    }
    if (code === "message_empty") {
      setMessageError(REQUEST_REFUSED.message_empty ?? null);
      messageRef.current?.focus();
      return;
    }
    if (code === "case_finished") {
      setFinished(true);
      void client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
      void client.invalidateQueries({ queryKey: CASES_KEY });
    }
    const sentence = code !== null ? REQUEST_REFUSED[code] : undefined;
    setRefused(
      sentence ?? (cause instanceof ProblemError ? cause.userMessage : "Anfrage nicht gesendet — Server nicht erreichbar."),
    );
    submitRef.current?.focus();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || held !== null || alreadyPending) return;
    // Both fields at once (S-5), the focus on the first one that is wrong.
    const text = message.trim();
    const noPerson = toUserId === "";
    const noMessage = text === "";
    if (noPerson || noMessage) {
      setToError(noPerson ? NO_PERSON : null);
      setMessageError(noMessage ? NO_MESSAGE : null);
      if (noPerson) focusTo();
      else messageRef.current?.focus();
      return;
    }
    // Kept before the answer: a refusal about this person resets the field (M-6).
    const asked = recipient?.displayName ?? null;
    setBusy(true);
    setRefused(null);
    try {
      const handover = await requestHandover(caseId, toUserId, text);
      refresh();
      onRequested(handover);
    } catch (cause) {
      if (refusalCode(cause) === "already_pending") {
        await alreadyRunning();
        return;
      }
      setBusy(false);
      refuse(cause, asked);
    }
  }

  const options = users.isPending
    ? [{ value: "", label: "Team wird geladen …" }]
    : teamDown
      ? [{ value: "", label: "Team nicht erreichbar" }]
      : nobody
        ? [{ value: "", label: "Niemand verfügbar" }]
        : // Says what to do, not „Bitte wählen" (UX gate round 1, #10).
          [
            { value: "", label: "Person wählen" },
            ...people.map((user) => ({
              value: user.id,
              // Who just declined, marked where the choice is made (#4).
              label: user.id === declinedBy ? `${user.displayName} (hat abgelehnt)` : user.displayName,
            })),
          ];

  const running = alreadyPending
    ? pendingRecipient !== null
      ? `Für diesen Vorgang läuft schon eine Anfrage an ${pendingRecipient}.`
      : (REQUEST_REFUSED.already_pending ?? "")
    : null;
  const alert = running ?? refused;
  // S-6 remainder (UX gate round 1): after the 409 no request of this dialog
  // goes out — the list below described one and still named the person
  // picked, as if it had. Said the other way round instead, and the list
  // gives way.
  const notSent =
    alreadyPending && recipient !== null ? `Deine Anfrage an ${recipient.displayName} ist nicht rausgegangen.` : null;

  // The refusal the person answers by handing over again (R3-12), over
  // „An". In the subtitle, which the dialog names as its description: read
  // out with the title when the dialog opens, not only by who looks.
  // After a lapse the same place says why that person is already chosen
  // (round-4 screen check, R4-14) — the day it ran out, as the consequences
  // below name the day the new one will: the event's moment is the request's
  // own `expires_at`, not when somebody noticed (`workflow.handover._expire`).
  const context =
    previous?.outcome === "declined" ? (
      <span className={styles.previous}>
        <b>{previous.name}</b> hat abgelehnt
        {previous.reason !== null ? (
          <>
            : „{previous.reason}“
          </>
        ) : null}{" "}
        · <Figures text={activityTime(previous.at)} />
      </span>
    ) : previous?.outcome === "expired" ? (
      <span className={styles.previous}>
        Die Anfrage an <b>{previous.name}</b> ist am <Figures text={formatDay(previous.at)} /> verfallen.
      </span>
    ) : null;

  return (
    <Dialog
      title="Vorgang übergeben"
      // The case number in the figure face, as everywhere else (UX gate round 1, #13).
      subtitle={
        <>
          {caseTitle} · <span className="num">{caseNo}</span>
          {/* A space, so the description reads „2026-0143 Jonas …", not „2026-0143Jonas". */}
          {context !== null ? " " : null}
          {context}
        </>
      }
      onClose={onClose}
      onClosed={onClosed}
      busy={busy}
    >
      <form className={styles.form} noValidate onSubmit={(event) => void submit(event)}>
        {/* Always the field, in every state of the list (record fix wave):
            nothing moves when the team arrives, and the dialog's focus
            starts here, not in the message below it. */}
        <SelectField
          id="handover-to"
          label="An"
          value={toUserId}
          options={options}
          error={toError}
          hint={teamDown ? TEAM_DOWN : nobody ? NOBODY : null}
          autoFocusTarget
          readOnly={alreadyPending}
          onChange={(value) => {
            setToError(null);
            setToUserId(value);
          }}
        />
        <TextArea
          id="handover-message"
          label="Nachricht (Pflicht)"
          value={message}
          onChange={(text) => {
            setMessageError(null);
            setMessage(text);
          }}
          rows={4}
          maxLength={2000}
          readOnly={alreadyPending}
          textareaRef={messageRef}
          error={messageError}
          // What the message is for (UX gate round 1, #10) — named once a
          // person is chosen; gone once nothing is to be written any more.
          hint={
            alreadyPending
              ? null
              : recipient !== null
                ? `Was muss ${recipient.displayName} wissen?`
                : "Was muss sie oder er wissen?"
          }
        />
        {alreadyPending ? null : (
          <div className={styles.consequencesBox}>
            <dl className={styles.consequences}>
              <dt>Bis zur Annahme</dt>
              <dd>bleibt der Vorgang bei dir.</dd>
              <dt>Nach der Annahme</dt>
              <dd>
                liegt er bei {recipient?.displayName ?? "der gewählten Person"} — du siehst ihn nicht mehr. Kalkulation
                und Freigabe gehen mit.
              </dd>
              <dt>Ohne Antwort</dt>
              <dd>
                verfällt die Anfrage am <span className="tabular">{dayInDays(7)}</span>.
              </dd>
            </dl>
            {/* The way back, said before it is needed (UX gate round 4, #6):
                the waiting line offers „Anfrage zurückziehen", and nobody
                knew that before sending. A sentence of its own under the
                three moments — it is not a fourth one. */}
            <p className={styles.consequenceNote}>Solange die Anfrage offen ist, kannst du sie zurückziehen.</p>
          </div>
        )}
        {/* The server's refusal in the one notice pattern (UX gate round 1,
            #3) — red edge, symbol and word, the only `role="alert"` here. */}
        {alert !== null ? (
          <StateMessage id="handover-refused" tone="conflict" live="alert" title={alert}>
            {notSent}
          </StateMessage>
        ) : null}
        <DialogFooter>
          {/* In its place in both states (R2-8): after a 409 it closes
              without the way to the running request. */}
          <Button
            onClick={() => {
              if (alreadyPending) onCancelRunning?.();
              onClose();
            }}
            aria-disabled={busy ? "true" : undefined}
          >
            Abbrechen
          </Button>
          {/* After a 409 `already_pending` the one way on is to the running
              request (S-6; UX gate round 1, #3: „die laufende ansehen") —
              closing puts the focus on the record's waiting line. The same
              element, so the focus that was on it stays. */}
          <Button
            ref={submitRef}
            type={alreadyPending ? "button" : "submit"}
            variant="primary"
            onClick={alreadyPending ? onClose : undefined}
            busy={!alreadyPending && busy}
            aria-disabled={!alreadyPending && (busy || held !== null) ? "true" : undefined}
            aria-describedby={alreadyPending ? undefined : (held ?? undefined)}
          >
            {alreadyPending ? "Laufende Anfrage ansehen" : busy ? "Wird angefragt …" : "Übergabe anfragen"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
